
import {Component} from "vue-property-decorator";
import {MainLayout} from "@/app";
import ApplicationService from "../service/Application";
import IApplicationForm from "../model/applicationForm/IApplicationForm";
import ApplicationForm from "../model/applicationForm/ApplicationForm";
import {LoginService} from "@/module/user";
import IApplication from "../model/IApplication";
import {ICommunicationError} from "@dev/hc-talk-talk";
import TemplateOptions from "./TemplateOptionsEnum";
import ApplicationFactory from "../model/applicationForm/ApplicationFactory";
import {HcPageModel} from "@dev/hc-components";
import router from "@/app/router";
import RouteName from "@/app/router/NameEnum";
import {notification} from "@/plugins";
import IToken from "@/module/user/dto/IToken";
import Customer from "@/module/main/components/Customer.vue";
import EnvSettings from "@/module/main/components/EnvSettings.vue";
import Loan from "@/module/main/components/Loan.vue";
import FinancedSubjectType from "@/module/main/dto/FinancedSubjectTypeEnum";
import Endpoints from "@/module/main/components/Endpoints.vue";
import ClientType from "@/module/main/dto/ClientTypeEnum";
import ClientTypeEnum from "@/module/main/dto/ClientTypeEnum";
import Culture from "@/module/main/page/CultureEnum";
import ParamsDocumentation from "@/app/layouts/ParamsDocumentation.vue";
import QrCode from "@/app/layouts/QrCode.vue";
import {
    applyStoredSelection,
    getTemplateForStoredEnv,
    getTemplateWithStoredSettings
} from "@/module/main/model/applicationForm/StoredSelection";

@Component({
    components: {
        QrCode,
        ParamsDocumentation,
        EnvSettings,
        Customer,
        MainLayout,
        Loan,
        Endpoints
    }
})
export default class Main extends HcPageModel {
    private formData: IApplicationForm = new ApplicationForm();
    private template: string = "";
    private myLoanURL: string = "";
    protected pageName: string = "application";
    protected showModalQr: boolean = false;

    private get showCustomer(): boolean
    {
        return this.formData.loan.financedSubjectType === FinancedSubjectType.LIABILITY
            || this.formData.loan.financedSubjectType === FinancedSubjectType.ORDER
            || (this.formData.loan.financedSubjectType === FinancedSubjectType.CASH
                && this.formData.loan.clientType === ClientTypeEnum.PERSON)
            || this.formData.loan.financedSubjectType === FinancedSubjectType.WALLET;
    }

    private sendForm(): void {
        this.postProcessData();
        this.$store.dispatch("main/setFinancedSubjectType", this.formData.loan.financedSubjectType);
        this.createInfoNotification("notification-token-request");

        LoginService.login(this.formData.envSettings)
            .then((token: IToken) => {
                this.createSuccessNotification("notification-token-received", token.accessToken);
                this.createApplication();
            })
            .catch((error: ICommunicationError) => {
                this.showError(error);
            });
    }

    private postProcessData(): void {
        this.formData.customer.block1.identificationNumberRaw
            = this.formData.customer.block1.identificationNumber?.replace(/_/g, "");
    }

    private createApplication(): void {
        this.createInfoNotification("notification-create-app-request");
        ApplicationService.createApplication(this.formData)
            .then((application: IApplication) => {
                this.createSuccessNotification("notification-create-app-received", application.id);
                if (!!application.gatewayRedirectUrl) {
                    this.myLoanURL = this.getMyLoanURL(application.gatewayRedirectUrl);
                    if (this.formData.envSettings.copyToClipboard) {
                      navigator.clipboard.writeText(this.myLoanURL)
                          .then(() => {
                            this.createSuccessNotification("notification-entryurl-copied-to-clipboard", this.myLoanURL);
                          })
                          .catch(() => {
                            this.createWarningNotification("notification-entryurl-not-copied-to-clipboard", this.myLoanURL);
                          });
                    }

                    if (this.formData.envSettings.showQrCode) {
                        this.showModalQr = true;
                    } else {
                        this.redirectToMyLoan && this.processRedirectToMyLoan(application.gatewayRedirectUrl);
                    }
                } else {
                    if (this.formData.endpoints.isReturnUrl === "true") {
                        this.createWarningNotification("notification-create-app-no-redirect-wfstep");
                    }
                }
            })
            .catch((error: ICommunicationError) => {
                this.showError(error);
            });
    }

    private get redirectToMyLoan(): boolean {
        let redirectToMyloan: string = this.$route.query["redirectToMyloan"] as string;

        return !(redirectToMyloan === "0" || redirectToMyloan === "false");
    }

    private showError(error: ICommunicationError): void {
        console.log(error);
        this.createErrorNotification("notification-error", error.toString());
    }

    private clearForm(): void {
        this.template = "";
        this.formData = new ApplicationForm();
    }

    private getMyLoanURL(defaultURL: string): string {
        let myLoanURL: string = defaultURL;
        let paramURL: string = this.$route.query["myloanRedirectUrl"] as string;

        if (paramURL) {
            let match = defaultURL.match(/^.*uuid=(.*)$/);
            match && (myLoanURL = paramURL.replace("{0}", match[1]));
        }

        return myLoanURL;
    }

    private setFormData(): void {
        this.loadTemplate(getTemplateForStoredEnv(this.$store.getters));
    }

    private loadTemplate(template: TemplateOptions): void {
        let culture: Culture;
        switch (template) {
            case TemplateOptions.CZ:
                culture = Culture.CZ;
                break;
            case TemplateOptions.SK:
                culture = Culture.SK;
                break;
            default:
                alert("Unknown template = " + template);
                return;
        }

        const storedClientType = this.getStoredClientType();
        this.template = template;
        this.formData = getTemplateWithStoredSettings(culture, this.$store.getters);
        this.storeAdjustedClientType(storedClientType);
        // env translated to the template country is kept after page reload
        this.storeEnv();
    }

    private financedSubjectTypeChanged(financedSubjectType: FinancedSubjectType): void
    {
        const storedClientType = this.getStoredClientType();
        applyStoredSelection(this.formData, financedSubjectType, this.$store.getters);
        this.storeAdjustedClientType(storedClientType);
    }

    private getStoredClientType(): ClientType
    {
        return this.$store.getters["main/getClientType"] || ClientType.PERSON;
    }

    private storeAdjustedClientType(storedClientType: ClientType): void
    {
        if (this.formData.loan.clientType !== storedClientType)
            this.$store.dispatch("main/setClientType", this.formData.loan.clientType);
    }

    private storeEnv(): void
    {
        this.$store.dispatch("main/setEnvType", this.formData.envSettings.envType);
        this.$store.dispatch("main/setEnv", this.formData.envSettings.env);
        this.$store.dispatch("main/setEnvBrand", this.formData.envSettings.brand);
    }

    private envSettingsChanged(): void
    {
        this.formData.endpoints = ApplicationFactory.getEndpoints(this.formData.envSettings.envType, this.formData.envSettings.env);
    }

    private financingApiVersionChanged(): void
    {
        this.financedSubjectTypeChanged(this.formData.loan.financedSubjectType);
    }

    private processRedirectToMyLoan(gatewayRedirectUrl: string): void {
        let myLoanURL: string = this.getMyLoanURL(gatewayRedirectUrl);
        this.createSuccessNotification("notification-redirect-to-mln");

        this.formData.envSettings.isEmbedded === "false"
            ? (window.location.href = myLoanURL)
            : router.push({name: RouteName.EXTERNAL_SITE_IN_LOKI, params: {url: myLoanURL}});
    }

    private createSuccessNotification(key: string, opt: string = ""): void {
        this.createNotification(key, opt, "success");
    }

    private createErrorNotification(key: string, opt: string = ""): void {
        this.createNotification(key, opt, "error");
    }

    private createWarningNotification(key: string, opt: string = ""): void {
        this.createNotification(key, opt, "warning");
    }

    private createInfoNotification(key: string, opt: string = ""): void {
        this.createNotification(key, opt, "info");
    }

    private createNotification(key: string, opt: string, status: string): void {
        notification.create(this.$localize(key, opt), this.$localize("common-ok"), "", "snackbar", status);
    }

    public created(): void {
        this.setFormData();
    }
}
